import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import Alert from '../Alert';
import { User, Users, Clock, Calendar as CalendarIcon, ArrowLeft, ChevronRight } from 'lucide-react';
import { formatDuration } from '../../utils/formatters';
import { StepIndicator } from '../ui/StepIndicator';
import '../../pages/MainLayout.css';

// We can define interfaces locally or import
interface Professional {
    id: number;
    name: string;
    photo: string | null;
    services: ProfessionalService[];
}

interface ProfessionalService {
    id: number;
    service_id: number; // The ID of the relationship or the service? Serializer says 'service' is ID.
    service_name: string;
    service_duration: string;
    default_price: string;
    custom_price: string | null;
    // We render 'price' based on custom or default logic usually, but let's stick to what's available
}

interface Service {
    // Legacy/Simplified interface for internal state if needed, 
    // but better to use the API structure
    id: number;
    name: string;
    duration: string;
    price: string;
}

interface BookingWizardProps {
    phone: string;
}

const BookingWizard: React.FC<BookingWizardProps> = ({ phone }) => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [professionals, setProfessionals] = useState<Professional[]>([]);

    // Selection State
    const [selectedProfessional, setSelectedProfessional] = useState<Professional | null>(null);
    const [selectedService, setSelectedService] = useState<Service | null>(null);
    const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
    const [selectedDate, setSelectedDate] = useState<string>('');
    const [availableSlots, setAvailableSlots] = useState<string[]>([]);

    // Beneficiary State
    const [isForDependent, setIsForDependent] = useState(false);
    const [dependentName, setDependentName] = useState('');
    const [dependents, setDependents] = useState<any[]>([]); // Store client dependents

    // Client Registration State (if new)
    const [clientName, setClientName] = useState('');
    const [isNewClient, setIsNewClient] = useState(false); // We check this step 

    // Loading/Error
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [alert, setAlert] = useState<{ show: boolean, message: string, type: 'success' | 'error' | 'warning' }>({ show: false, message: '', type: 'success' });

    // Fetch Professionals on Mount
    useEffect(() => {
        const fetchProfessionals = async () => {
            try {
                // Use relative URL assuming proxy or base URL configured, or absolute for now
                // Ideally use an api utility
                const res = await axios.get(`${API_BASE_URL}/api/public/professionals/`);
                setProfessionals(res.data);
            } catch (err) {
                console.error(err);
                setError('Erro ao carregar profissionais.');
            }
        };
        fetchProfessionals();

        // Check if client exists
        const checkClient = async () => {
            try {
                const res = await axios.post(`${API_BASE_URL}/api/public/auth/check-phone/`, { phone });
                if (!res.data.exists) {
                    setIsNewClient(true);
                } else {
                    setClientName(res.data.client.name);
                    if (res.data.client.dependents) {
                        setDependents(res.data.client.dependents);
                    }
                }
            } catch (err) {
                console.error(err);
            }
        };
        checkClient();
    }, [phone]);

    // Fetch Slots when Date/Professional changes (now we fetch 7 days whenever service is picked)
    useEffect(() => {
        const fetchSlots = async () => {
            if (selectedProfessional && step === 3) {
                try {
                    setLoading(true);
                    // No date param needed for default 7 days, or we could pass start_date
                    const res = await axios.get(`${API_BASE_URL}/api/public/professionals/${selectedProfessional.id}/slots/`);
                    setAvailableSlots(res.data);
                } catch (err) {
                    console.error(err);
                } finally {
                    setLoading(false);
                }
            }
        };
        fetchSlots();
    }, [selectedProfessional, step]);

    const handleConfirm = async () => {
        if (!selectedProfessional || !selectedService || !selectedSlot) return; // selectedDate is implicit in selectedSlot now (iso)

        if (isNewClient && !clientName) {
            setError("Por favor preencha seu nome.");
            return;
        }
        if (isForDependent && !dependentName) {
            setError("Por favor preencha o nome do dependente.");
            return;
        }

        try {
            setLoading(true);
            const payload = {
                professional_id: selectedProfessional.id,
                service_id: selectedService.id,
                // Construct ISO datetime from date and slot time
                start_time: selectedSlot, // It is already ISO from API
                client_phone: phone,
                client_name: isNewClient ? clientName : undefined,
                dependent_name: isForDependent ? dependentName : undefined
            };

            await axios.post(`${API_BASE_URL}/api/public/appointments/book/`, payload);

            setAlert({
                show: true,
                message: 'Agendamento realizado com sucesso!',
                type: 'success'
            });
        } catch (err) {
            console.error(err);
            setError('Erro ao realizar agendamento.');
        } finally {
            setLoading(false);
        }
    };

    const getVisualStep = (realStep: number): number => {
        if (realStep === 1) return 1;
        if (realStep === 3) return 2;
        if (realStep === 4) return 3;
        return 1;
    };

    const handleStepClick = (visualStep: number) => {
        const stepMap: Record<number, number> = { 1: 1, 2: 3, 3: 4 };
        const targetRealStep = stepMap[visualStep];
        
        if (targetRealStep < step) {
            setStep(targetRealStep);
            if (targetRealStep === 1) {
                setSelectedProfessional(null);
                setSelectedService(null);
            }
        }
    };

    // Render Steps
    if (step === 1) {
        return (
            <div className="list-container border-0 shadow-none bg-transparent">
                <StepIndicator
                    steps={[
                        { label: 'Serviço' },
                        { label: 'Horário' },
                        { label: 'Finalizar' }
                    ]}
                    currentStep={getVisualStep(step)}
                    onStepClick={handleStepClick}
                    showLabels={true}
                    completedIcon="checkmark"
                />
                <h2 className="text-center mb-5" style={{ fontSize: '1.75rem' }}>O que vamos fazer hoje?</h2>
                <div className="professionals-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
                    {professionals.map(prof => (
                        <div key={prof.id} className="professional-card glass-effect" style={{ padding: '2rem', borderRadius: 'var(--radius-lg)', background: 'white' }}>
                            <div className="text-center mb-4">
                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                    {prof.photo ? (
                                        <img src={prof.photo} alt={prof.name} style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', border: '4px solid white', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} />
                                    ) : (
                                        <div style={{ width: '100px', height: '100px', borderRadius: '50%', background: 'linear-gradient(135deg, #E7E5E4 0%, #D6D3D1 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem', color: '#78716C', border: '4px solid white', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}>
                                            {prof.name.charAt(0)}
                                        </div>
                                    )}
                                </div>
                                <h3 className="mt-3 mb-1" style={{ fontSize: '1.25rem' }}>{prof.name}</h3>
                                <p className="text-muted small" style={{ letterSpacing: '0.05em' }}>ESPECIALISTA</p>
                            </div>

                            <div className="services-list" style={{ display: 'grid', gap: '0.75rem' }}>
                                {prof.services.map((svc: any) => (
                                    <button
                                        key={svc.id}
                                        className="service-btn"
                                        onClick={() => {
                                            setSelectedProfessional(prof);
                                            setSelectedService({
                                                id: svc.service,
                                                name: svc.service_name,
                                                duration: svc.service_duration,
                                                price: svc.custom_price || svc.default_price
                                            });
                                            setStep(3);
                                        }}
                                    >
                                        <div>
                                            <div style={{ fontWeight: 600 }}>{svc.service_name}</div>
                                            <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: 400 }}>
                                                <Clock size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                                                {formatDuration(svc.service_duration)}
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <span style={{ color: 'var(--accent-color)', fontWeight: 700 }}>R$ {svc.custom_price || svc.default_price}</span>
                                            <ChevronRight size={16} className="text-muted" />
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (step === 3) {
        return (
            <div className="list-container border-0 shadow-none bg-transparent">
                <StepIndicator
                    steps={[
                        { label: 'Serviço' },
                        { label: 'Horário' },
                        { label: 'Finalizar' }
                    ]}
                    currentStep={getVisualStep(step)}
                    onStepClick={handleStepClick}
                    showLabels={true}
                    completedIcon="checkmark"
                />
                <button className="back-btn mb-4" onClick={() => { setStep(1); setSelectedProfessional(null); setSelectedService(null); }}>
                    <ArrowLeft size={18} /> Voltar para serviços
                </button>

                <div className="glass-effect mb-5" style={{ background: 'white', padding: '1.5rem', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    {selectedProfessional?.photo ? (
                        <img src={selectedProfessional.photo} alt={selectedProfessional.name} style={{ width: '50px', height: '50px', borderRadius: '50%', objectFit: 'cover' }} />
                    ) : (
                        <div style={{ width: '50px', height: '50px', borderRadius: '50%', background: 'var(--bg-color)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', color: 'var(--text-muted)' }}>
                            {selectedProfessional?.name.charAt(0)}
                        </div>
                    )}
                    <div>
                        <h3 style={{ margin: '0 0 0.15rem 0', fontSize: '1.1rem' }}>{selectedProfessional?.name}</h3>
                        <p style={{ margin: 0, color: 'var(--accent-color)', fontWeight: 600, fontSize: '0.85rem' }}>{selectedService?.name}</p>
                    </div>
                </div>

                <h2 className="text-center mb-5">Escolha o melhor horário</h2>

                <div className="slots-container" style={{ display: 'grid', gap: '2.5rem' }}>
                    {loading && <div className="text-center p-5"><p>Buscando disponibilidades...</p></div>}
                    {!loading && availableSlots.length === 0 && (
                        <div className="text-center p-5 glass-effect" style={{ background: 'white', borderRadius: 'var(--radius-md)' }}>
                            <p className="text-muted">Infelizmente não temos horários disponíveis para os próximos 7 dias.</p>
                        </div>
                    )}

                    {!loading && (Object.entries(availableSlots.reduce((groups: any, slot: any) => {
                        const date = slot.iso_datetime.split('T')[0];
                        if (!groups[date]) groups[date] = [];
                        groups[date].push(slot);
                        return groups;
                    }, {} as Record<string, any[]>)) as [string, any[]][]).map(([date, slots]) => {
                        const dateObj = new Date(date + 'T12:00:00');
                        const header = dateObj.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: '2-digit' });
                        const headerFormatted = header.charAt(0).toUpperCase() + header.slice(1);

                        return (
                            <div key={date}>
                                <h4 style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: 'var(--secondary-color)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{headerFormatted}</h4>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: '12px' }}>
                                    {slots.map((slot: any, index: number) => (
                                        <button
                                            key={index}
                                            className={`slot-btn ${selectedSlot === slot.iso_datetime ? 'active' : ''}`}
                                            onClick={() => {
                                                setSelectedSlot(slot.iso_datetime);
                                                setSelectedDate(date);
                                                setStep(4);
                                            }}
                                        >
                                            {slot.iso_datetime.split('T')[1].substring(0, 5)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    if (step === 4) {
        return (
            <div className="form-container glass-effect border-0">
                <StepIndicator
                    steps={[
                        { label: 'Serviço' },
                        { label: 'Horário' },
                        { label: 'Finalizar' }
                    ]}
                    currentStep={getVisualStep(step)}
                    onStepClick={handleStepClick}
                    showLabels={true}
                    completedIcon="checkmark"
                />
                <button className="back-btn mb-4" onClick={() => setStep(3)}>
                    <ArrowLeft size={18} /> Alterar horário
                </button>
                <h2 className="mb-4">Finalizar Agendamento</h2>

                <div className="summary mb-5" style={{ background: 'var(--bg-color)', border: '1px solid var(--border-color)', padding: '2rem', borderRadius: 'var(--radius-md)', display: 'flex', gap: '2rem', alignItems: 'center' }}>
                    {selectedProfessional?.photo ? (
                        <img src={selectedProfessional.photo} alt={selectedProfessional.name} style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', border: '3px solid white' }} />
                    ) : (
                        <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', color: 'var(--text-muted)' }}>
                            {selectedProfessional?.name.charAt(0)}
                        </div>
                    )}
                    <div>
                        <h3 className="mb-2" style={{ fontSize: '1.25rem' }}>{selectedProfessional?.name}</h3>
                        <div style={{ display: 'grid', gap: '0.5rem' }}>
                            <p className="m-0 small text-muted"><Clock size={14} className="me-2" /> <strong>Serviço:</strong> {selectedService?.name}</p>
                            <p className="m-0 small text-muted"><CalendarIcon size={14} className="me-2" /> <strong>Data:</strong> {selectedDate.split('-').reverse().join('/')} às {selectedSlot?.split('T')[1].substring(0, 5)}</p>
                        </div>
                    </div>
                </div>

                <div className="form-group mb-4">
                    <label className="mb-3 d-block fw-bold text-uppercase small tracking-widest text-muted">Para quem é o agendamento?</label>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <button
                            type="button"
                            className={`flex-fill p-3 rounded-3 text-center transition-all ${!isForDependent ? 'glass-effect border-2' : 'bg-white border text-muted'}`}
                            style={{ border: !isForDependent ? '2px solid var(--accent-color)' : '1px solid var(--border-color)', background: !isForDependent ? 'white' : 'white' }}
                            onClick={() => setIsForDependent(false)}
                        >
                            <User size={18} className="mb-2 d-block mx-auto" />
                            <span className="small fw-bold">Para Mim</span>
                        </button>
                        <button
                            type="button"
                            className={`flex-fill p-3 rounded-3 text-center transition-all ${isForDependent ? 'glass-effect border-2' : 'bg-white border text-muted'}`}
                            style={{ border: isForDependent ? '2px solid var(--accent-color)' : '1px solid var(--border-color)', background: isForDependent ? 'white' : 'white' }}
                            onClick={() => setIsForDependent(true)}
                        >
                            <Users size={18} className="mb-2 d-block mx-auto" />
                            <span className="small fw-bold">Outra Pessoa</span>
                        </button>
                    </div>
                </div>

                {isForDependent && (
                    <div className="form-group mb-4">
                        <label className="mb-2 d-block fw-bold text-uppercase small tracking-widest text-muted">Nome do Atendido</label>
                        {dependents.length > 0 && (
                            <div className="mb-3 d-flex flex-wrap gap-2">
                                {dependents.map((dep: any) => (
                                    <button
                                        key={dep.id}
                                        type="button"
                                        className={`px-3 py-2 rounded-pill small transition-all ${dependentName === dep.name ? 'bg-dark text-white' : 'bg-white border'}`}
                                        onClick={() => setDependentName(dep.name)}
                                    >
                                        {dep.name}
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    className={`px-3 py-2 rounded-pill small transition-all ${(!dependents.some((d: any) => d.name === dependentName) && dependentName !== '') ? 'bg-dark text-white' : 'bg-white border'}`}
                                    onClick={() => setDependentName('')}
                                >
                                    + Outro
                                </button>
                            </div>
                        )}
                        {(!dependents.some((d: any) => d.name === dependentName) || dependents.length === 0) && (
                            <input
                                type="text"
                                value={dependentName}
                                onChange={e => setDependentName(e.target.value)}
                                className="form-control"
                                placeholder="Nome completo do dependente"
                            />
                        )}
                    </div>
                )}

                {isNewClient && (
                    <div className="form-group mb-4">
                        <label className="mb-2 d-block fw-bold text-uppercase small tracking-widest text-muted">Seu Nome Completo</label>
                        <input type="text" value={clientName} onChange={e => setClientName(e.target.value)} className="form-control" placeholder="Para seu cadastro no sistema" />
                    </div>
                )}

                {error && <div className="alert alert-danger mb-4 py-2 small">{error}</div>}

                <button className="btn-primary-premium w-100 py-3 mt-2" onClick={handleConfirm} disabled={loading}>
                    {loading ? 'Confirmando...' : 'Confirmar Agendamento'}
                </button>

                {alert.show && (
                    <Alert
                        show={true}
                        message={alert.message}
                        type={alert.type}
                        onClose={() => {
                            setAlert({ ...alert, show: false });
                            if (alert.type === 'success') {
                                navigate(`/public/menu?phone=${phone}`);
                            }
                        }}
                    />
                )}
            </div>
        );
    }

    return <div>Passo desconhecido</div>;
};

export default BookingWizard;
